import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntradaEditarOrganizacion, Organizacion } from '@copathy/shared';
import { api, ErrorApi } from '../lib/api';
import { usePuede } from '../lib/auth';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Combo } from '../componentes/Combo';
import { PAISES, banderaUrl } from '../lib/paises';

/**
 * Datos de la organización — la ficha de Copathy.
 *
 * ── Dos bloques, y la separación no es decorativa ──
 * **Identidad y contacto** es lo operativo: el nombre que se pinta en la
 * plataforma, el correo al que escribe quien tiene un problema. **Relato de
 * marca** es la misión, la visión y los valores.
 *
 * Van separados porque se editan en momentos distintos —el teléfono cambia, la
 * misión no— y porque el segundo bloque **hoy no lo muestra ninguna pantalla**.
 * Presentarlos juntos haría creer que la misión sale en algún lado.
 */
export function PanelOrganizacion() {
  const qc = useQueryClient();
  const puedeEditar = usePuede('empresas.editar');

  const consulta = useQuery({
    queryKey: ['organizacion'],
    queryFn: () => api.get<Organizacion>('/api/organizacion'),
  });

  const guardar = useMutation({
    mutationFn: (cambios: EntradaEditarOrganizacion) =>
      api.patch<Organizacion>('/api/organizacion', cambios),
    onSuccess: (o) => qc.setQueryData(['organizacion'], o),
  });

  if (consulta.isPending) {
    return <p className="py-16 text-center text-marino-400">Cargando…</p>;
  }
  if (consulta.isError || !consulta.data) {
    return (
      <p className="py-16 text-center text-red-600">
        No pudimos cargar los datos de la organización.
      </p>
    );
  }

  return (
    <Formulario
      datos={consulta.data}
      editable={puedeEditar}
      guardando={guardar.isPending}
      alGuardar={(c) => guardar.mutateAsync(c)}
    />
  );
}

type Borrador = {
  nombre: string;
  rif: string;
  descripcion: string;
  pais: string | null;
  direccion: string;
  correo: string;
  telefono: string;
  sitioWeb: string;
  instagram: string;
  mision: string;
  vision: string;
  valores: string;
  historia: string;
};

const aBorrador = (o: Organizacion): Borrador => ({
  nombre: o.nombre,
  rif: o.rif ?? '',
  descripcion: o.descripcion ?? '',
  pais: o.pais,
  direccion: o.direccion ?? '',
  correo: o.correo ?? '',
  telefono: o.telefono ?? '',
  sitioWeb: o.sitioWeb ?? '',
  instagram: o.instagram ?? '',
  mision: o.mision ?? '',
  vision: o.vision ?? '',
  valores: o.valores ?? '',
  historia: o.historia ?? '',
});

function Formulario({
  datos,
  editable,
  guardando,
  alGuardar,
}: {
  datos: Organizacion;
  editable: boolean;
  guardando: boolean;
  alGuardar: (cambios: EntradaEditarOrganizacion) => Promise<unknown>;
}) {
  const [f, setF] = useState<Borrador>(() => aBorrador(datos));
  const [error, setError] = useState<string | null>(null);
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [guardado, setGuardado] = useState(false);

  // Si el servidor devuelve algo distinto de lo enviado —un recorte de espacios,
  // el país en minúsculas—, el formulario tiene que reflejar lo que quedó
  // guardado y no lo que se tecleó.
  useEffect(() => setF(aBorrador(datos)), [datos]);

  const original = aBorrador(datos);
  const hayCambios = (Object.keys(original) as (keyof Borrador)[]).some((k) => f[k] !== original[k]);

  const cambiar = <K extends keyof Borrador>(k: K, v: Borrador[K]) => {
    setGuardado(false);
    setF((previo) => ({ ...previo, [k]: v }));
  };

  async function enviar() {
    setError(null);
    setCampos({});
    /*
      Solo se manda lo que cambió, y **la cadena vacía viaja como `null`**: es la
      forma de borrar un campo. Mandarla como '' guardaría una cadena vacía, que
      después hay que distinguir de «no hay dato» en cada consulta.
    */
    const cambios: Record<string, unknown> = {};
    for (const k of Object.keys(original) as (keyof Borrador)[]) {
      if (f[k] === original[k]) continue;
      cambios[k] = f[k] === '' ? null : f[k];
    }

    try {
      await alGuardar(cambios as EntradaEditarOrganizacion);
      setGuardado(true);
    } catch (e) {
      if (e instanceof ErrorApi) {
        setError(e.message);
        setCampos(e.campos ?? {});
        return;
      }
      setError('No pudimos guardar los cambios. Intenta de nuevo.');
    }
  }

  const soloLectura = !editable;

  return (
    <div className="space-y-4">
      {soloLectura && (
        <p className="rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-sm text-marino-500">
          Puedes ver estos datos, pero no cambiarlos.
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <section className="rounded-2xl border border-marino-100 bg-white p-5">
        <h2 className="text-base font-semibold text-marino-800">Identidad y contacto</h2>
        <p className="mt-0.5 text-sm text-marino-400">
          Lo que se ve en la plataforma y por dónde te escriben.
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Nombre"
            value={f.nombre}
            onChange={(e) => cambiar('nombre', e.target.value)}
            error={campos.nombre}
            disabled={soloLectura}
          />
          <Campo
            etiqueta="Identificación fiscal"
            value={f.rif}
            onChange={(e) => cambiar('rif', e.target.value)}
            error={campos.rif}
            disabled={soloLectura}
          />
          <div className="sm:col-span-2">
            <Campo
              etiqueta="A qué se dedica"
              value={f.descripcion}
              onChange={(e) => cambiar('descripcion', e.target.value)}
              error={campos.descripcion}
              disabled={soloLectura}
            />
          </div>
          <Combo
            etiqueta="País"
            opciones={PAISES.map((p) => ({
              clave: p.iso,
              texto: p.nombre,
              icono: (
                <img src={banderaUrl(p.iso)} alt="" loading="lazy" className="h-3 w-4.5 rounded-[2px] object-cover" />
              ),
            }))}
            valor={f.pais}
            alCambiar={(v) => cambiar('pais', v === null ? null : String(v))}
            vacio="Sin especificar"
            error={campos.pais}
          />
          <Campo
            etiqueta="Dirección"
            value={f.direccion}
            onChange={(e) => cambiar('direccion', e.target.value)}
            error={campos.direccion}
            disabled={soloLectura}
          />
          <Campo
            etiqueta="Correo de contacto"
            type="email"
            value={f.correo}
            onChange={(e) => cambiar('correo', e.target.value)}
            error={campos.correo}
            disabled={soloLectura}
          />
          <Campo
            etiqueta="Teléfono"
            value={f.telefono}
            onChange={(e) => cambiar('telefono', e.target.value)}
            error={campos.telefono}
            disabled={soloLectura}
          />
          <Campo
            etiqueta="Sitio web"
            value={f.sitioWeb}
            onChange={(e) => cambiar('sitioWeb', e.target.value)}
            error={campos.sitioWeb}
            disabled={soloLectura}
          />
          <Campo
            etiqueta="Instagram"
            value={f.instagram}
            onChange={(e) => cambiar('instagram', e.target.value)}
            error={campos.instagram}
            disabled={soloLectura}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-marino-100 bg-white p-5">
        <h2 className="text-base font-semibold text-marino-800">Relato de marca</h2>
        <p className="mt-0.5 text-sm text-marino-400">
          Se conserva desde la aplicación anterior.{' '}
          <strong className="font-semibold text-marino-600">
            Todavía no lo muestra ninguna pantalla
          </strong>{' '}
          — está guardado para cuando haga falta.
        </p>

        <div className="mt-5 space-y-4">
          <AreaTexto
            etiqueta="Misión"
            valor={f.mision}
            filas={3}
            soloLectura={soloLectura}
            error={campos.mision}
            alCambiar={(v) => cambiar('mision', v)}
          />
          <AreaTexto
            etiqueta="Visión"
            valor={f.vision}
            filas={3}
            soloLectura={soloLectura}
            error={campos.vision}
            alCambiar={(v) => cambiar('vision', v)}
          />
          <AreaTexto
            etiqueta="Valores"
            valor={f.valores}
            filas={6}
            soloLectura={soloLectura}
            error={campos.valores}
            alCambiar={(v) => cambiar('valores', v)}
          />
          <AreaTexto
            etiqueta="Historia"
            valor={f.historia}
            filas={7}
            soloLectura={soloLectura}
            error={campos.historia}
            alCambiar={(v) => cambiar('historia', v)}
          />
        </div>
      </section>

      {editable && (
        /*
          La barra de guardar se pega abajo: con dos bloques y cuatro áreas de
          texto, un botón al final del documento queda a dos pantallas de scroll
          del campo que se acaba de tocar.
        */
        <div className="sticky bottom-4 flex items-center justify-end gap-3 rounded-2xl border border-marino-100 bg-white/95 px-4 py-3 backdrop-blur">
          {guardado && !hayCambios && (
            <span className="text-sm font-semibold text-marino-500">Guardado.</span>
          )}
          {hayCambios && (
            <Boton
              variante="secundario"
              className="px-3.5 py-2 text-sm"
              onClick={() => setF(original)}
            >
              Descartar
            </Boton>
          )}
          <Boton
            className="px-4 py-2 text-sm"
            disabled={!hayCambios}
            cargando={guardando}
            onClick={() => void enviar()}
          >
            Guardar cambios
          </Boton>
        </div>
      )}
    </div>
  );
}

/**
 * `Campo` es de una línea con etiqueta flotante, y la historia son diez
 * renglones. Esto conserva el mismo lenguaje visual —radio, borde, foco marino—
 * sin forzar un `<input>` a ser lo que no es.
 */
function AreaTexto({
  etiqueta,
  valor,
  filas,
  soloLectura,
  error,
  alCambiar,
}: {
  etiqueta: string;
  valor: string;
  filas: number;
  soloLectura: boolean;
  error?: string | undefined;
  alCambiar: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-marino-700">{etiqueta}</span>
      <textarea
        rows={filas}
        value={valor}
        disabled={soloLectura}
        onChange={(e) => alCambiar(e.target.value)}
        className={`w-full resize-y rounded-2xl border bg-white px-4 py-3 text-sm text-marino-800
          transition placeholder:text-marino-300 disabled:bg-marino-50/60
          focus:outline-none focus:ring-4 focus:ring-marino-700/10
          ${error ? 'border-red-300 focus:border-red-400' : 'border-marino-200 focus:border-marino-400'}`}
      />
      {error && <span className="mt-1 block text-sm text-red-600">{error}</span>}
    </label>
  );
}
